import { localStorageHelper } from '../../../../utils/localStorageHelper'

/**
 * Default preferences for Portfolio Analysis. Shipped values represent the
 * baseline rendering for first-time users (and anyone who hasn't opened
 * Preferences). Decimal places default to 2 across all measure kinds, which
 * is a deliberate change from the platform formatter defaults (0 for
 * marketValue, 1 for returns) -- driven by SMT-1617's "default two decimal
 * places for all" requirement.
 */
export const DEFAULT_PREFERENCES = Object.freeze({
  styling: {
    headerMode: 'wrap',
    zebra: false,
    density: 'md'
  },
  display: {
    useAccountNicknames: true,
    growthColors: true,
    showCurrencySymbol: true,
    negativeFormat: 'parens',
    decimals: {
      currency: 2,
      return: 2,
      weight: 2,
      ratio: 2
    }
  }
})

function preferencesKey (userId) {
  return `portfolioExplorer.${userId}.preferences`
}

/**
 * Fills missing nested keys with defaults so adding new preferences later
 * doesn't require a migration. Stored values for present keys win.
 */
export function mergePreferences (stored) {
  const src = stored && typeof stored === 'object' ? stored : {}
  return {
    styling: {
      ...DEFAULT_PREFERENCES.styling,
      ...(src.styling || {})
    },
    display: {
      ...DEFAULT_PREFERENCES.display,
      ...(src.display || {}),
      decimals: {
        ...DEFAULT_PREFERENCES.display.decimals,
        ...(src.display?.decimals || {})
      }
    }
  }
}

export function getPreferences (userId) {
  if (userId == null) return mergePreferences(null)
  const raw = localStorageHelper.tryLoad(preferencesKey(userId))
  return mergePreferences(raw)
}

/**
 * Persists prefs for the given user. Callers typically pass the full merged
 * shape (e.g. from PreferencesDialog's Apply action), which has the side
 * benefit of normalizing old partial blobs in storage on every write -- any
 * pre-existing entry missing a field will be overwritten with one that has
 * defaults filled in. No explicit migration step is required.
 */
export function setPreferences (userId, prefs) {
  if (userId == null) return
  localStorageHelper.tryStore(preferencesKey(userId), prefs)
}

export function clearPreferences (userId) {
  if (userId == null) return
  localStorageHelper.tryStore(preferencesKey(userId), null)
}
